import { getNcrTypeLabel, isNcrType } from "./nonConformance";
import { getReportAuthorEmail } from "./reportAuthor";
import {
	getReportAuthorName,
	getReportPlaceParts,
	getReportProjectCode,
	getStreamRecordId,
	getStreamRecordLabel,
	getStreamRecordName,
	StreamPlaceReport,
	StreamPlaces,
} from "./reportStream";

// The fields of a non-conformance report the NCR stream reads.
export type StreamNcr = StreamPlaceReport & {
	report_number?: unknown;
	title?: unknown;
	requirements?: unknown;
	description?: unknown;
	ncr_type?: unknown;
	// The department's id, as the report form stores it.
	department?: unknown;
	operator?: string;
	primary_author?: string | null;
	author?: string | null;
};

// The NCR's type, when it is one the report form offers. A report with
// another type or none shows no tag and isn't in the type filter.
export const getNcrType = (report: StreamNcr) =>
	isNcrType(report.ncr_type) ? report.ncr_type : undefined;

// The NCR's department, when it is stored as an id.
export const getNcrDepartmentId = (report: StreamNcr) =>
	getStreamRecordId(report.department);

// How the department filter names a department: by its name, or as an
// unknown department while the departments load, or for one deleted or in a
// tenant the user can't read.
export const getNcrDepartmentLabel = (
	id: string,
	departmentMap: Record<string, any>
) => getStreamRecordLabel(departmentMap, id, "Unknown department");

// The line under an NCR's title: its type, then its department and where it
// belongs.
export function getNcrMeta(
	report: StreamNcr,
	places: StreamPlaces,
	departmentMap: Record<string, any>
) {
	const type = getNcrType(report);
	const department = getStreamRecordName(departmentMap, report.department);
	return {
		tag: type === undefined ? undefined : getNcrTypeLabel(type),
		parts: [
			...(department === undefined ? [] : [department]),
			...getReportPlaceParts(report, places),
		],
	};
}

// What the stream's search looks through for an NCR.
export function getNcrSearchTexts(
	report: StreamNcr,
	places: StreamPlaces,
	departmentMap: Record<string, any>,
	authorNames: Record<string, string>,
	getTenantName: (tenantKey: string) => string | undefined
) {
	const { tag, parts } = getNcrMeta(report, places, departmentMap);
	return [
		report.report_number,
		report.title,
		report.requirements,
		report.description,
		tag,
		...parts,
		getReportProjectCode(report, places),
		getReportAuthorEmail(report),
		getReportAuthorName(report, authorNames),
		report.operator ? getTenantName(report.operator) : undefined,
	];
}
